// components
import CustomNotification from "../components/CustomNotification";
import Footer from "../components/Footer";
import { notify } from "../components/Toast";

import { makeStyles, Toaster, useToastController } from "@fluentui/react-components";
import { Warning24Filled } from "@fluentui/react-icons";
import * as React from "react";

// screens
import EditorWarning from "./EditorWarning";
import Home from "./Home";
import Login from "./Login";
import SaveReport from "./SaveReport";

// styles
import { colors } from "../styles";

// utils
import { attemptTokenRefresh, clearStorage } from "../../utils/auth";
import { Screen } from "../../utils/common/constants";
import { Utils } from "../../utils/common/utils";

const useStyles = makeStyles({
  root: {
    "minHeight": "100vh",
    "fontFamily": "Poppins",
    "display": "flex",
    "flexDirection": "column",
    "--fontFamilyBase": "Poppins",
    "--colorBrandBackground": colors.fundwave,
    "--colorBrandBackgroundHover": colors.fundwave
  },
  main: {
    padding: "20px 15px",
    backgroundColor: colors.primaryBackground,
    flex: "1",
    display: "flex",
    flexDirection: "column",
    gap: "15px"
  }
});

const App: React.FC<{ screen?: Screen }> = ({ screen }) => {
  const styles = useStyles();
  const { dispatchToast } = useToastController();

  const [activeScreen, setActiveScreen] = React.useState(screen ?? Screen.Login);
  const [updateAvailable, setUpdateAvailable] = React.useState(false);
  const sessionExpired = React.useRef(false);

  function updateEventListner(e: any) {
    setUpdateAvailable(!!e.detail);
  }

  React.useEffect(() => {
    document.addEventListener("fw-update", updateEventListner);

    Utils.prepareOpenAPIClient(Utils.APIClient.fundwave);

    return () => document.removeEventListener("fw-update", updateEventListner);
  }, []);

  React.useEffect(() => {
    async function handleLoggedOut() {

      const isTokenValid = await attemptTokenRefresh("api_unauthorized");
      if (isTokenValid) return;

      if (sessionExpired.current) return;
      sessionExpired.current = true;

      clearStorage("api_unauthorized");
      setActiveScreen(Screen.Login);
      dispatchToast(notify("Session expired. Please sign in again."), { intent: "error" });
    }

    document.addEventListener("logged-out", handleLoggedOut);

    return () => document.removeEventListener("logged-out", handleLoggedOut);
  }, [dispatchToast]);

  function navigate(screen: Screen) {
    if (!localStorage.getItem("refreshToken")) {
      clearStorage("session_timed_out");
      setActiveScreen(Screen.Login);
      dispatchToast(notify("Session timed out!"), { intent: "error" });
      return;
    }
    setActiveScreen(screen);
  }

  return (
    <>
      <div className={styles.root}>
        {/* body */}
        <section className={styles.main}>
          {localStorage.getItem("client") === "excel" && updateAvailable && (
            <CustomNotification label="Please update to unlock your workbook" icon={<Warning24Filled />} btnLabel="UPDATE" onBtnClick={() => openUpdateDialog()} />
          )}
          {activeScreen === Screen.Login && <Login />}
          {activeScreen === Screen.Home && <Home navigate={navigate} />}
          {activeScreen === Screen.Report && localStorage.getItem("client") === "excel" && <SaveReport navigate={navigate} />}
          {activeScreen === Screen.EditorWarning && <EditorWarning />}
        </section>

        <Footer activeScreen={activeScreen} />
      </div>
      <Toaster position="top" offset={{ vertical: 60 }} />
    </>
  );
};

function openUpdateDialog() {
  const customEvent = new CustomEvent("fw-show-dialog", { bubbles: true });
  document.dispatchEvent(customEvent);
}

export default App;
